import { useEffect, useLayoutEffect, useRef } from 'react'

import { useNavigationType, useParams } from 'react-router-dom'
import useSnackBarContext from '@contexts/snackBarContext'
import { useAuthContext } from '@contexts/authContext'
import { CSSTransition } from 'react-transition-group'
import useSessionState from '@hooks/useSessionState'
import useStateRef from 'react-usestateref'
import useLoading from '@hooks/useLoading'
import axios from '@utils/webRequests'
import { size } from '@styles/theme'

import { SocialProfileRepostsFeed } from '@components/social/SocialProfileRepostsFeed'
import { SocialProfileRepliesFeed } from '@components/social/SocialProfileRepliesFeed'
import { SocialProfileMediaFeed } from '@components/social/SocialProfileMediaFeed'
import { SocialProfilePostsFeed } from '@components/social/SocialProfilePostsFeed'
import SocialProfileTopbar from '@components/social/SocialProfileTopbar'
import SocialProfileMain from '@components/social/SocialProfileMain'
import ScreenContainer from '@screens/ScreenContainer'
import SwipeableViews from 'react-swipeable-views'

const SocialProfile = ({ }) => {
  const params = useParams()
  const navigationType = useNavigationType()
  const [selectedTab, setSelectedTab] = useSessionState(`social-profile_selected-tab_${params?.id}`, 0, (storedVal, defaultVal) => navigationType === 'POP' ? storedVal : defaultVal)
  const [profile, setProfile] = useSessionState(`social-profile_user-data_${params?.id}`, null)
  const [nodeEl, setNodeEl, nodeRef] = useStateRef(null)

  const { isLoading: isFollowLoading, setIsLoading: setIsFollowLoading, isLoadingDelayed: isFollowLoadingDelayed } = useLoading(false)
  const { state: authState } = useAuthContext()
  const { snackError } = useSnackBarContext()
  const isOwnProfile = authState?.user?.uuid === profile?.uuid

  // gets user on inititial render
  useLayoutEffect(() => {
    if (profile?.uuid === params.id) return;
    axios.get(`/api/users/${params?.id}/social_profile`)
      .then((res) => setProfile(res.data))
      .catch(console.err)
  }, [params.id])

  // Navigates to top on forward navigation
  useEffect(() => {
    if (navigationType !== 'POP' && nodeEl) {
      nodeEl.scroll({ top: 0 })
    }
  }, [params.id, nodeEl])

  const followUser = () => {
    if (isFollowLoading) return;
    setIsFollowLoading(true)
    setProfile({ ...profile, imFollowing: true, followerCount: profile.followerCount + 1 })
    axios.post(`/api/my/users/${profile.uuid}/follows`)
      .catch((error) => {
        // render snack error
        snackError({ error, fallbackMessage: 'Couldn\'t follow user' })
        // undo state change
        setProfile(profile)
      })
      .finally(() => setIsFollowLoading(false))
  }

  const unfollowUser = () => {
    if (isFollowLoading) return;
    setIsFollowLoading(true)
    setProfile({ ...profile, imFollowing: false, followerCount: profile.followerCount - 1 })
    axios.delete(`/api/my/users/${profile.uuid}/follows`)
      .catch((error) => {
        // render snack error
        snackError({ error, fallbackMessage: 'Couldn\'t unfollow user' })
        // undo state change
        setProfile(profile)
      })
      .finally(() => setIsFollowLoading(false))
  }

  return (
    <CSSTransition
      in
      appear
      nodeRef={nodeRef}
      timeout={300}
      classNames='animation__modal'>
      <>
        <SocialProfileTopbar
          followUser={followUser}
          unfollowUser={unfollowUser}
          profile={isOwnProfile && authState?.user ? authState?.user : profile}
          setProfile={setProfile}
          isOwnProfile={isOwnProfile}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
        <ScreenContainer
          ref={node => node && setNodeEl(node)}
          style={{ backgroundcolor: 'black', overflowY: 'auto', overflowX: 'hidden' }}>
          {/* Anchor for topbar rendering */}
          <div id='social-profile_topbar-threshold' style={{ position: 'relative', top: size(7) }} />
            <SocialProfileMain
              setSelectedTab={setSelectedTab}
              selectedTab={selectedTab}
              isOwnProfile={isOwnProfile}
              unfollowUser={unfollowUser}
              followUser={followUser}
              profile={isOwnProfile && authState?.user ? authState?.user : profile}
            />
            {/* Anchor for topbar-tabs rendering */}
            <div id='social-profile_tabs-threshold' style={{ position: 'relative', bottom: size(13) }} />
            <SwipeableViews
              // We need every div created by this to not clip or scroll it's inner contents
              // for react virtuoso to work with the screen container scrolling
              containerStyle={{ overflow: 'visible', height: 'auto' }}
              style={{ overflow: 'visible', height: 'auto' }}
              slideStyle={{ overflow: 'visible', height: 'auto' }}
              onChangeIndex={(i) => setSelectedTab(i)}
              index={selectedTab}
              enableMouseEvents
              axis='x'>
              <SocialProfilePostsFeed
                followUser={followUser}
                unfollowUser={unfollowUser}
                profile={profile}
                isOwnProfile={isOwnProfile}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                customScrollParent={nodeEl}
              />
              <SocialProfileRepostsFeed
                followUser={followUser}
                unfollowUser={unfollowUser}
                profile={profile}
                isOwnProfile={isOwnProfile}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                customScrollParent={nodeEl}
              />
              <SocialProfileRepliesFeed
                followUser={followUser}
                unfollowUser={unfollowUser}
                profile={profile}
                isOwnProfile={isOwnProfile}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                customScrollParent={nodeEl}
              />
              <SocialProfileMediaFeed
                followUser={followUser}
                unfollowUser={unfollowUser}
                profile={profile}
                isOwnProfile={isOwnProfile}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                customScrollParent={nodeEl}
              />
            </SwipeableViews>
        </ScreenContainer>
      </>
    </CSSTransition>
  )
}

export { SocialProfile }
